import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Box, Card, Chip, IconButton, List, ListItem, ListItemText,
  Switch, Typography, FormControlLabel, Stack, Button, TextField,
  FormControl, InputLabel, Select, MenuItem, Alert, CardContent, ListItemIcon,
} from '@mui/material';
import {
  Notifications as NotificationsIcon, Close as CloseIcon,
  Settings as SettingsIcon, Info as InfoIcon, Warning as WarningIcon,
  Check as CheckIcon, Delete as DeleteIcon, MarkEmailRead as MarkReadIcon,
  ShoppingCart as OrderIcon, LocalOffer as OfferIcon,
} from '@mui/icons-material';
import axios from '../api';
import PaginationControls from '../components/PaginationControls';

interface Notification {
  _id: string;
  type: 'info' | 'warning' | 'success';
  title: string;
  message: string;
  createdAt: string;
  read: boolean;
  user?: { name?: string; email?: string };
}

// Customer mock notifications
const mockCustomerNotifications: Notification[] = [
  {
    _id: '1',
    type: 'success',
    title: 'Order Ready for Pickup',
    message: 'Your order #ORD-001 from Muscat Bakery is ready for pickup between 5:00 PM - 7:00 PM today.',
    createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    read: false,
  },
  {
    _id: '2',
    type: 'info',
    title: 'New Surprise Box Available',
    message: 'Fresh Pasta Box now available at Al Khuwair Restaurant - 60% off!',
    createdAt: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
    read: false,
  },
  {
    _id: '3',
    type: 'info',
    title: 'Favorite Merchant Update',
    message: 'Qurum Cafe just added 3 new surprise boxes to their menu.',
    createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    read: true,
  },
  {
    _id: '4',
    type: 'success',
    title: 'Order Completed',
    message: 'Thank you for picking up your order #ORD-002! You saved OMR 4.50 and helped reduce food waste.',
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
    read: true,
  },
];

export default function Notifications() {
  const { user } = useAuth();
  const isCustomer = user?.role === 'customer';
  const isMerchant = user?.role === 'merchant';
  const isDelivery = user?.role === 'delivery';
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    orders: true,
    businesses: true,
    payments: true,
    reviews: true,
    system: true,
  });
  const [audience, setAudience] = useState('customers');
  const [pincode, setPincode] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<{ severity: 'success' | 'error'; text: string } | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const fetchNotifications = useCallback(async () => {
    try {
      if (isCustomer) {
        // Fetch from backend API
        const response = await axios.get('/api/notifications', {
          params: { page, limit: rowsPerPage }
        });

        if (response.data.success) {
          setNotifications(response.data.notifications || []);
          setTotal(response.data.total || 0);
        } else {
          // Fallback to mock data for customers
          setNotifications(mockCustomerNotifications);
          setTotal(mockCustomerNotifications.length);
        }
      } else if (isMerchant || isDelivery) {
        // Fetch merchant/delivery partner's received notifications (order updates, etc.)
        const response = await axios.get('/api/notifications', {
          params: { page, limit: rowsPerPage }
        });

        if (response.data.success) {
          setNotifications(response.data.notifications || []);
          setTotal(response.data.total || 0);
        } else {
          // Fallback to mock data if API fails
          setNotifications(mockCustomerNotifications);
          setTotal(mockCustomerNotifications.length);
        }
      } else {
        const response = await axios.get('/api/operations/notifications', { params: { page, limit: rowsPerPage } });
        setNotifications(response.data.notifications || []);
        setTotal(response.data.total ?? response.data.notifications?.length ?? 0);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      // Use mock data as fallback
      if (isCustomer) {
        setNotifications(mockCustomerNotifications);
        setTotal(mockCustomerNotifications.length);
      }
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, isCustomer, isMerchant, isDelivery, user]);

  useEffect(() => {
    fetchNotifications();
    // Notification settings live on the admin-only operations API; only admins render that panel.
    if (!isCustomer && !isMerchant && !isDelivery) {
      fetchSettings();
    }
  }, [fetchNotifications, isCustomer, isMerchant, isDelivery]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/operations/settings');
      if (response.data.notificationSettings) {
        setSettings(response.data.notificationSettings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const removeNotification = async (_id: string) => {
    try {
      if (isCustomer) {
        await axios.delete(`/api/notifications/${_id}`);
      } else {
        await axios.delete(`/api/operations/notifications/${_id}`);
      }
      setNotifications(notifications.filter(n => n._id !== _id));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleMarkAsRead = async (_id: string) => {
    try {
      await axios.put(`/api/notifications/${_id}/read`);
      setNotifications((current) =>
        current.map((notif) => (notif._id === _id ? { ...notif, read: true } : notif))
      );
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await axios.put('/api/notifications/mark-all-read');
      setNotifications((current) =>
        current.map((notif) => ({ ...notif, read: true }))
      );
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleClearAll = async () => {
    if (window.confirm('Are you sure you want to clear all notifications?')) {
      try {
        await axios.delete('/api/notifications');
        setNotifications([]);
      } catch (error) {
        console.error('Error clearing notifications:', error);
      }
    }
  };

  const handleSettingChange = async (setting: string, value: boolean) => {
    const newSettings = { ...settings, [setting]: value };
    setSettings(newSettings);

    try {
      await axios.put('/api/operations/settings', {
        notificationSettings: newSettings,
      });
    } catch (error) {
      console.error('Error updating settings:', error);
    }
  };

  const handleSendNotification = async () => {
    if (!title.trim() || !message.trim() || (audience === 'pincode' && !pincode.trim())) return;
    setSending(true);
    setSendResult(null);
    try {
      const result = await axios.post('/api/operations/notifications', {
        type: 'system',
        severity: 'info',
        title,
        message,
        audience,
        pincode: audience === 'pincode' ? pincode : undefined,
      });
      const names = result.data.recipientNames || [];
      const recipientSummary = names.length > 0
        ? ` Recipients: ${names.join(', ')}.`
        : ' No matching recipients were found.';
      setSendResult({ severity: 'success', text: `Notification sent to ${result.data.recipients} recipient${result.data.recipients === 1 ? '' : 's'}.${recipientSummary}` });
      setTitle('');
      setMessage('');
      fetchNotifications();
    } catch (error) {
      setSendResult({ severity: 'error', text: 'Notification could not be sent.' });
    } finally {
      setSending(false);
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const getIcon = (type: string) => {
    switch (type) {
      case 'warning': return <WarningIcon />;
      case 'success': return <CheckIcon />;
      default: return <InfoIcon />;
    }
  };

  const getColor = (type: string) => {
    switch (type) {
      case 'warning': return 'warning';
      case 'success': return 'success';
      default: return 'info';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Notifications</Typography>
          <Typography color="text.secondary">
            {isCustomer
              ? 'Stay updated with your orders and new offers'
              : isMerchant
              ? 'Order updates and system notifications'
              : isDelivery
              ? 'New delivery assignments and updates'
              : `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}`}
          </Typography>
        </Box>
        {(isCustomer || isMerchant || isDelivery) ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {unreadCount > 0 && (
              <Button
                variant="outlined"
                startIcon={<MarkReadIcon />}
                onClick={handleMarkAllAsRead}
                size="small"
              >
                Mark All Read
              </Button>
            )}
            {notifications.length > 0 && (
              <Button variant="outlined" color="error" size="small" onClick={handleClearAll}>
                Clear All
              </Button>
            )}
          </Box>
        ) : (
          <Chip
            icon={<NotificationsIcon />}
            label={unreadCount.toString()}
            color={unreadCount > 0 ? 'error' : 'default'}
            variant="outlined"
          />
        )}
      </Box>

      {(isCustomer || isMerchant || isDelivery) && (
        <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #0B6E4F 0%, #B1D65D 100%)', color: 'white' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <NotificationsIcon sx={{ fontSize: 48 }} />
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  {unreadCount} Unread Notification{unreadCount !== 1 ? 's' : ''}
                </Typography>
                <Typography sx={{ opacity: 0.9 }}>
                  {notifications.length} total notification{notifications.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {!isCustomer && !isMerchant && !isDelivery && (
        <Card sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Send notification</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
          <TextField label="Title" value={title} onChange={(event) => setTitle(event.target.value)} fullWidth />
          <FormControl fullWidth>
            <InputLabel id="notification-audience-label">Send to</InputLabel>
            <Select labelId="notification-audience-label" value={audience} label="Send to" onChange={(event) => setAudience(event.target.value)}>
              <MenuItem value="customers">All customers</MenuItem>
              <MenuItem value="merchants">All merchants</MenuItem>
              <MenuItem value="pincode">Users in a pincode</MenuItem>
              <MenuItem value="new_users">Users who joined in the last 10 days</MenuItem>
              <MenuItem value="all">Everyone</MenuItem>
            </Select>
          </FormControl>
          {audience === 'pincode' && (
            <TextField label="Pincode" value={pincode} onChange={(event) => setPincode(event.target.value)} fullWidth />
          )}
          <TextField label="Message" value={message} onChange={(event) => setMessage(event.target.value)} fullWidth multiline minRows={2} sx={{ gridColumn: { md: '1 / -1' } }} />
        </Box>
        {sendResult && <Alert severity={sendResult.severity} sx={{ mt: 2 }}>{sendResult.text}</Alert>}
          <Button variant="contained" sx={{ mt: 2 }} onClick={handleSendNotification} disabled={sending || !title.trim() || !message.trim() || (audience === 'pincode' && !pincode.trim())}>
            {sending ? 'Sending...' : 'Send notification'}
          </Button>
        </Card>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: (isCustomer || isMerchant || isDelivery) ? '1fr' : '2fr 1fr' }, gap: 3 }}>
        {/* Notifications List */}
        <Box>
          <Card>
            <List sx={{ p: 0 }}>
              {loading && (
                <ListItem>
                  <ListItemText primary="Loading notifications..." />
                </ListItem>
              )}
              {!loading && notifications.length === 0 && (
                <ListItem>
                  <ListItemText
                    primary="No notifications"
                    secondary="All caught up! You'll see notifications here."
                  />
                </ListItem>
              )}
              {!loading && notifications.map((notification, index) => (
                <ListItem
                  key={notification._id}
                  sx={{
                    bgcolor: !notification.read ? 'action.hover' : 'transparent',
                    borderBottom: index < notifications.length - 1 ? '1px solid #eee' : 'none',
                    '&:hover': { bgcolor: 'action.hover' },
                    py: 2,
                    px: 3,
                  }}
                  secondaryAction={
                    isCustomer ? (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {!notification.read && (
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleMarkAsRead(notification._id)}
                            title="Mark as read"
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          edge="end"
                          size="small"
                          color="error"
                          onClick={() => removeNotification(notification._id)}
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    ) : (
                      <IconButton
                        size="small"
                        onClick={() => removeNotification(notification._id)}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )
                  }
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%', gap: 2 }}>
                    {(isCustomer || isMerchant || isDelivery) && (
                      <ListItemIcon>
                        <Box
                          sx={{
                            bgcolor: getColor(notification.type) === 'success' ? 'success.main' : getColor(notification.type) === 'warning' ? 'warning.main' : 'primary.main',
                            borderRadius: '50%',
                            p: 1.5,
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          {notification.type === 'success' ? <OrderIcon /> : <OfferIcon />}
                        </Box>
                      </ListItemIcon>
                    )}
                    {!isCustomer && !isMerchant && !isDelivery && (
                      <Chip
                        icon={getIcon(notification.type)}
                        label={notification.type}
                        color={getColor(notification.type)}
                        size="small"
                        sx={{ mt: 0.5 }}
                      />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography sx={{ fontWeight: notification.read ? 400 : 700 }}>
                          {notification.title}
                        </Typography>
                        {!notification.read && (isCustomer || isMerchant) && (
                          <Chip label="New" color="primary" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                        {!notification.read && !isCustomer && !isMerchant && (
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-block',
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: 'primary.main',
                              ml: 1,
                              mt: -0.5,
                            }}
                          />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {notification.message}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(notification.createdAt).toLocaleString()}
                      </Typography>
                      {notification.user?.name && !isCustomer && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          Recipient: {notification.user.name}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </ListItem>
              ))}
            </List>
          </Card>
          {!isCustomer && (
            <PaginationControls page={page} total={total} rowsPerPage={rowsPerPage} onPageChange={setPage} onRowsPerPageChange={(value) => { setRowsPerPage(value); setPage(1); }} />
          )}
        </Box>

        {/* Notification Settings */}
        {!isCustomer && !isMerchant && !isDelivery && (
        <Box>
          <Card sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <SettingsIcon />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Notification Settings</Typography>
            </Box>

            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.orders}
                    onChange={(e) => handleSettingChange('orders', e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Order Alerts</Typography>
                    <Typography variant="caption" color="text.secondary">New orders & status updates</Typography>
                  </Box>
                }
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.businesses}
                    onChange={(e) => handleSettingChange('businesses', e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Business Updates</Typography>
                    <Typography variant="caption" color="text.secondary">Verifications & applications</Typography>
                  </Box>
                }
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.payments}
                    onChange={(e) => handleSettingChange('payments', e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Payment Alerts</Typography>
                    <Typography variant="caption" color="text.secondary">Failed payments & disputes</Typography>
                  </Box>
                }
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.reviews}
                    onChange={(e) => handleSettingChange('reviews', e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Review Notifications</Typography>
                    <Typography variant="caption" color="text.secondary">New reviews & feedback</Typography>
                  </Box>
                }
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={settings.system}
                    onChange={(e) => handleSettingChange('system', e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>System Alerts</Typography>
                    <Typography variant="caption" color="text.secondary">System & maintenance updates</Typography>
                  </Box>
                }
              />
            </Stack>
          </Card>
        </Box>
        )}
      </Box>
    </Box>
  );
}
